import React, { createContext, useState, useContext, ReactNode } from 'react';

/** What can be attached to a chat message (server src/agents/attachments.py),
 * sent as {id, type}. */
export type AttachmentType = 'legal_act' | 'case_law_digest' | 'doctrine' | 'firm_document';

export interface AttachedDocument {
  id: string;
  title: string;
  type: AttachmentType;
  doc_type: string;
  file_name: string;
}

interface AttachmentContextType {
  attachedDocuments: AttachedDocument[];
  attachDocument: (document: AttachedDocument) => void;
  // Ids are per table: pass the type so that act 5 and doctrine 5 stay apart
  // (without it, any attachment with that id matches).
  detachDocument: (documentId: string, type?: AttachmentType) => void;
  clearAttachments: () => void;
  isDocumentAttached: (documentId: string, type?: AttachmentType) => boolean;
  addDocument: (document: AttachedDocument) => void;
  removeDocument: (documentId: string, type: AttachmentType) => void;
}

export const AttachmentContext = createContext<AttachmentContextType | undefined>(undefined);

const matches = (doc: AttachedDocument, documentId: string, type?: AttachmentType) =>
  doc.id === documentId && (type === undefined || doc.type === type);

export const AttachmentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [attachedDocuments, setAttachedDocuments] = useState<AttachedDocument[]>([]);

  const attachDocument = (document: AttachedDocument) => {
    setAttachedDocuments(prev => {
      // Avoid duplicates
      if (prev.some(doc => matches(doc, document.id, document.type))) {
        return prev;
      }
      return [...prev, document];
    });
  };

  const detachDocument = (documentId: string, type?: AttachmentType) => {
    setAttachedDocuments(prev => prev.filter(doc => !matches(doc, documentId, type)));
  };

  const clearAttachments = () => {
    setAttachedDocuments([]);
  };

  const isDocumentAttached = (documentId: string, type?: AttachmentType) => {
    return attachedDocuments.some(doc => matches(doc, documentId, type));
  };

  // Alias for backward compatibility
  const addDocument = attachDocument;
  const removeDocument = (documentId: string, type: AttachmentType) => {
    detachDocument(documentId, type);
  };

  return (
    <AttachmentContext.Provider
      value={{
        attachedDocuments,
        attachDocument,
        detachDocument,
        clearAttachments,
        isDocumentAttached,
        addDocument,
        removeDocument,
      }}
    >
      {children}
    </AttachmentContext.Provider>
  );
};

export const useAttachments = () => {
  const context = useContext(AttachmentContext);
  if (!context) {
    throw new Error('useAttachments must be used within an AttachmentProvider');
  }
  return context;
};
